import { searchAgents } from '@cronos-app/api';
import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/config/ui';

import { useGlobalChainLayer } from '../useGlobalChainLayer';
import { Agent } from './useAgents';

type PageParam =
  | undefined
  | {
      first: number;
      skip: number;
    };

export type useSearchAgentsOptions = UseInfiniteQueryOptions<Agent[]> & {
  n?: number;
  search: string;
};

export const useSearchAgents = ({
  n = 10,
  search,
  ...options
}: useSearchAgentsOptions) => {
  const chainLayer = useGlobalChainLayer();

  return useInfiniteQuery<Agent[]>({
    queryKey: [useSearchAgents.queryKey, n, chainLayer, search],
    queryFn: async ({ pageParam: _pageParam }) => {
      const pageParam: NonNullable<PageParam> = _pageParam || {
        first: n,
        skip: 0,
      };

      const res = await searchAgents(
        chainLayer,
        search,
        pageParam.first,
        pageParam.skip,
      );

      return res.content.map((agent) => new Agent(agent));
    },
    getNextPageParam: (lastPage, allPages): PageParam => {
      if (lastPage.length < n) {
        // there is no next page available.
        return undefined;
      }

      return {
        first: n,
        skip: allPages.length * n,
      };
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: RefetchInterval.Fast,
    ...options,
    enabled: options.enabled === false ? false : !!search,
  });
};
useSearchAgents.queryKey = 'useSearchAgents';
